import { Download, Loader2, Mail, MessageCircle } from 'lucide-react';
import { MODE_LABEL, type DeliveryMode } from './share-care-plan-modal.types';

type ShareCarePlanChoiceStepProps = {
  onChoose: (mode: DeliveryMode) => void;
  onPreviewPdf: () => void;
  isPdfDownloading: boolean;
};

/**
 * Step 1 of the Share Care Plan modal: pick the delivery method (Email
 * vs. In-app) or jump straight to a PDF preview. The preview link reuses
 * the same authenticated download handler the top-level "Download care
 * plan" button uses, so it never hits the 401 a plain new-tab open would.
 */
export const ShareCarePlanChoiceStep = ({
  onChoose,
  onPreviewPdf,
  isPdfDownloading,
}: ShareCarePlanChoiceStepProps) => (
  <div className="space-y-3">
    <button
      type="button"
      onClick={() => onChoose('email')}
      className="flex w-full items-start gap-3 rounded-lg border border-gray-200 p-4 text-left transition-colors hover:border-[#026857] hover:bg-sage-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900">
        <Mail className="h-4 w-4" aria-hidden />
      </span>
      <div>
        <p className="font-medium text-gray-900">{MODE_LABEL.email}</p>
        <p className="text-sm text-gray-600 mt-0.5">
          Send a link to the plan plus a PDF attachment to your client&apos;s inbox.
        </p>
      </div>
    </button>

    <button
      type="button"
      onClick={() => onChoose('in-app')}
      className="flex w-full items-start gap-3 rounded-lg border border-gray-200 p-4 text-left transition-colors hover:border-[#026857] hover:bg-sage-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-sage text-white">
        <MessageCircle className="h-4 w-4" aria-hidden />
      </span>
      <div>
        <p className="font-medium text-gray-900">{MODE_LABEL['in-app']}</p>
        <p className="text-sm text-gray-600 mt-0.5">
          Drop the plan into your conversation with the client in bQuest.
        </p>
      </div>
    </button>

    <div className="pt-2">
      <button
        type="button"
        onClick={onPreviewPdf}
        disabled={isPdfDownloading}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-sage transition-colors hover:text-[hsl(169_95%_18%)] disabled:opacity-60"
      >
        {isPdfDownloading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <Download className="h-3.5 w-3.5" aria-hidden />
        )}
        {isPdfDownloading ? 'Preparing care guide…' : 'Preview the PDF'}
      </button>
    </div>
  </div>
);
